import React, { useEffect, useState } from "react";
import { Eye, Loader2, RotateCcw, Save } from "lucide-react";
import { api } from "./api";
import { useConfirmation } from "./Confirmation";
import { BoutonSombre, Modale, Section } from "./ui";

/* Les e-mails envoyés aux familles : sujet et texte modifiables, champs automatiques entre
   accolades, aperçu réel et retour au texte d'origine. Les parties techniques (mise en page
   1000 ARTS, bouton WhatsApp, bouton du numéro AVS, PDF joint, QR-facture) restent
   fabriquées par la console. */
export default function Gabarits() {
  const { informer, demander } = useConfirmation();
  const [gabarits, setGabarits] = useState(null);
  const [ouvert, setOuvert] = useState("");
  const [brouillon, setBrouillon] = useState({});
  const [apercu, setApercu] = useState(null);
  const [occupe, setOccupe] = useState("");
  const [err, setErr] = useState("");

  const charger = async () => {
    try {
      const d = await api("/gabarits");
      setGabarits(d.gabarits);
    } catch (e) { setErr(e.message); }
  };
  useEffect(() => { charger(); }, []);

  const ouvrir = (g) => {
    setOuvert(ouvert === g.cle ? "" : g.cle);
    setBrouillon({ sujet: g.sujet, texte: g.texte });
  };

  const enregistrer = async (cle) => {
    setOccupe(cle);
    setErr("");
    try {
      await api(`/gabarits/${cle}`, { method: "PUT", body: JSON.stringify(brouillon) });
      await charger();
      await informer({ titre: "E-mail enregistré", ton: "succes",
        texte: "Le nouveau texte sera utilisé au prochain envoi." });
    } catch (e) { setErr(e.message); }
    setOccupe("");
  };

  const retablir = async (cle) => {
    if (!await demander({ titre: "Rétablir le texte d'origine", ton: "attention",
      texte: "Ton texte sera remplacé par celui d'origine de la console.",
      valider: "Rétablir" })) return;
    setOccupe(cle);
    try {
      const r = await api(`/gabarits/${cle}`, { method: "DELETE" });
      setBrouillon({ sujet: r.sujet, texte: r.texte });
      await charger();
    } catch (e) { setErr(e.message); }
    setOccupe("");
  };

  const voir = async (cle) => {
    setOccupe(cle);
    try {
      setApercu(await api(`/gabarits/${cle}/apercu`));
    } catch (e) { setErr(e.message); }
    setOccupe("");
  };

  return (
    <Section titre="E-mails envoyés aux familles" testid="reglages-gabarits"
      aide="Relis et modifie le texte avant d'envoyer. Les champs entre accolades sont remplacés automatiquement ; le reste (mise en page 1000 ARTS, bouton du groupe WhatsApp, bouton du numéro AVS, PDF joint, QR-facture) est fabriqué par la console.">
      {err && <p className="text-sm" style={{ color: "#B91C1C" }}>{err}</p>}
      {!gabarits && <p className="text-sm text-[var(--muted)]">Chargement…</p>}
      <div className="space-y-3">
        {(gabarits || []).map((g) => (
          <div key={g.cle} className="bg-white rounded-2xl border p-4" style={{ borderColor: "var(--border)" }}
            data-testid={`gabarit-${g.cle}`}>
            <div className="flex flex-wrap items-center gap-3">
              <div>
                <div className="font-semibold">{g.titre}
                  {g.modifie && <span className="text-xs font-normal" style={{ color: "var(--primary)" }}> · texte modifié par l'école</span>}
                </div>
                <div className="text-xs text-[var(--muted)]">{g.quand}</div>
              </div>
              <div className="ml-auto flex flex-wrap gap-2">
                <button className="btn-ghost" onClick={() => voir(g.cle)} disabled={occupe === g.cle}
                  data-testid={`gabarit-apercu-${g.cle}`}>
                  {occupe === g.cle ? <Loader2 size={13} className="animate-spin" /> : <Eye size={13} />} Aperçu
                </button>
                <button className="btn-ghost" onClick={() => ouvrir(g)} data-testid={`gabarit-modifier-${g.cle}`}>
                  {ouvert === g.cle ? "Fermer" : "Modifier le texte"}
                </button>
              </div>
            </div>

            {ouvert === g.cle && (
              <div className="mt-4 space-y-3">
                <label className="block">
                  <span className="text-[10px] font-semibold uppercase tracking-[0.14em] text-[var(--muted)]">Objet de l'e-mail</span>
                  <input className="input-field mt-1" value={brouillon.sujet}
                    onChange={(e) => setBrouillon({ ...brouillon, sujet: e.target.value })}
                    data-testid={`gabarit-sujet-${g.cle}`} />
                </label>
                <label className="block">
                  <span className="text-[10px] font-semibold uppercase tracking-[0.14em] text-[var(--muted)]">Texte du message</span>
                  <textarea className="input-field mt-1" rows={9} value={brouillon.texte}
                    onChange={(e) => setBrouillon({ ...brouillon, texte: e.target.value })}
                    data-testid={`gabarit-texte-${g.cle}`} />
                  <span className="block text-xs text-[var(--muted)] mt-1">
                    Chaque ligne est un paragraphe · **entre deux étoiles** met en gras · une ligne qui commence par « &gt; » va dans l'encadré (montant, horaire…).
                  </span>
                </label>
                <div className="text-xs text-[var(--muted)]">
                  Champs disponibles : {g.champs.map((c) => (
                    <code key={c} className="mx-0.5 px-1 rounded" style={{ background: "#F1F5F9" }}>{c}</code>
                  ))}
                </div>
                <div className="flex flex-wrap gap-2">
                  <BoutonSombre onClick={() => enregistrer(g.cle)} disabled={occupe === g.cle}
                    testid={`gabarit-enregistrer-${g.cle}`}>
                    <Save size={14} /> Enregistrer
                  </BoutonSombre>
                  <button className="btn-ghost" onClick={() => retablir(g.cle)} disabled={occupe === g.cle}
                    data-testid={`gabarit-retablir-${g.cle}`}>
                    <RotateCcw size={13} /> Rétablir le texte d'origine
                  </button>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      {apercu && (
        <Modale large titre="Aperçu de l'e-mail" sous_titre={`Objet : ${apercu.sujet}`}
          onFermer={() => setApercu(null)} testid="modale-apercu-gabarit">
          <div className="rounded-xl border p-4 bg-white" style={{ borderColor: "var(--border)" }}
            data-testid="apercu-gabarit-html" dangerouslySetInnerHTML={{ __html: apercu.html }} />
          <p className="text-xs text-[var(--muted)] mt-3">
            Exemple de données : les champs seront remplacés par les vraies valeurs à l'envoi.
          </p>
        </Modale>
      )}
    </Section>
  );
}
